import React, { useEffect, useState, useRef } from 'react';
import { getConfig } from '../config';

declare global {
  interface Window {
    google: any;
    googleRetryCount: number;
  }
}

interface GoogleAuthProps {
  onSuccess: (credential: string) => void;
  onError: (error: any) => void;
}

export default function GoogleAuth({ onSuccess, onError }: GoogleAuthProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonElement, setButtonElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let maxTimeoutId: NodeJS.Timeout;
    let initRetryCount = 0;

    const initializeGoogleAuth = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        clearTimeout(maxTimeoutId);
        try {
          window.google.accounts.id.initialize({
            client_id: getConfig().googleClientId,
            callback: (response: any) => {
              if (response.credential) {
                onSuccess(response.credential);
              } else {
                console.error('No credential in response:', response);
                onError('No credential received from Google');
              }
            }
          });
          
          // Check DOM readiness - use buttonElement state
          if (buttonElement) {
            try {
              window.google.accounts.id.renderButton(
                buttonElement,
                {
                  theme: 'filled_black',
                  size: 'large',
                  text: 'continue_with',
                  width: 300,
                  shape: 'rectangular'
                }
              );
              setIsLoading(false);
              return; // Exit early on success
            } catch (renderError) {
              console.error('Error rendering Google button:', renderError);
              setErrorMessage('Failed to render Google sign-in button');
              onError('Failed to render Google sign-in button');
              setIsLoading(false);
              return;
            }
          }
          
          setIsLoading(false);
          clearTimeout(timeoutId);
        } catch (error) {
          console.error('Error initializing Google Auth:', error);
          setErrorMessage(`Failed to initialize Google Auth: ${error}`);
          onError(`Failed to initialize Google Auth: ${error}`);
          setIsLoading(false);
          clearTimeout(timeoutId);
        }
      } else {
        initRetryCount++;

        if (initRetryCount > 150) { // 15 seconds worth of retries
          console.error('Google Auth: too many retries');
          setErrorMessage('Google API failed to load after multiple attempts');
          onError('Google API failed to load');
          setIsLoading(false);
          return;
        }
        
        // Use minimal retry delay
        timeoutId = setTimeout(initializeGoogleAuth, 50);
      }
    };

    const loadScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        initializeGoogleAuth();
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Google Identity Services script', error);
        setErrorMessage('Failed to load Google sign-in script. Please check your internet connection and try again.');
        onError('Failed to load Google Identity Services');
        setIsLoading(false);
      };

      document.head.appendChild(script);
    };

    // Set a timeout to prevent infinite loading
    maxTimeoutId = setTimeout(() => {
      console.error('Google Auth initialization timed out');
      setErrorMessage('Google sign-in is taking too long to load. Please refresh the page.');
      setIsLoading(false);
      onError('Google Auth initialization timed out');
    }, 20000); // 20 seconds

    // Check if Google script is already loaded
    if (window.google && window.google.accounts && window.google.accounts.id) {
      initializeGoogleAuth();
      return () => clearTimeout(maxTimeoutId);
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      initializeGoogleAuth();
      return () => {
        clearTimeout(maxTimeoutId);
        clearTimeout(timeoutId);
      };
    }

    // Load the script
    loadScript();

    return () => {
      clearTimeout(maxTimeoutId);
      clearTimeout(timeoutId);
    };
  }, [onSuccess, onError, buttonElement]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mb-2"></div>
        <p className="text-sm text-gray-500">Loading Google sign-in...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="text-red-400 text-sm mb-2">{errorMessage}</div>
        <button
          onClick={() => window.location.reload()}
          className="text-blue-400 hover:text-blue-300 text-sm underline"
        >
          Refresh page
        </button>
      </div>
    );
  }

  const setRef = (element: HTMLDivElement | null) => {
    setButtonElement(element);
  };

  return (
    <div className="flex justify-center">
      <div ref={setRef}></div>
    </div>
  );
}